import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { NavigationService, NavItem } from './services/navigation.service';
import { NavigationEnd, Router } from '@angular/router';
import { UserService } from '../../services/user.service';
import { Subject } from 'rxjs';
import { filter, takeUntil, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { UserRoleService } from '../../core/services/role.service';
import { UserAvatarService } from './services/user-avatar.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface User {
  name: string;
  email: string;
  img?: string | null;
}

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent implements OnInit, OnDestroy {
  isHovered = false;
  isClickBlocked = false;
  isCollapsed = false;
  suppressHover = false;

  @Output() isCollapsedEvent = new EventEmitter<boolean>();

  sections: NavItem[] = [];
  accountSections: NavItem[] = [];
  userRole = '';

  userName = 'Test User';
  userEmail = 'test@gmail.com';
  userImg = '../../assets/icons/navbar/nav-user-icon.svg';

  expandedItems: Set<string> = new Set();

  private iconCache = new Map<string, SafeHtml>();
  private activeLinkCache = new Map<string, boolean>();
  private currentUrl = '';

  private readonly COLLAPSE_WIDTH = 1255;
  private readonly SUMMARY_KEY = 'SummaryData';
  private readonly RETURN_TRANSFER_KEY = 'returnTransferSummaryData';

  private destroy$ = new Subject<void>();

  constructor(
    private navService: NavigationService,
    private userService: UserService,
    private router: Router,
    private userRoleService: UserRoleService,
    private userAvatarService: UserAvatarService,
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.currentUrl = this.router.url;
    this.expandCurrentRouteMenu();

    this.userService
      .getUserInfo()
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) =>
          prev?.name === curr?.name && prev?.email === curr?.email && prev?.img === curr?.img
        )
      )
      .subscribe((user: User | null) => {
        if (user) {
          this.userName = user.name ?? this.userName;
          this.userEmail = user.email ?? this.userEmail;
          this.updateUserAvatar(user.img ?? undefined);
        } else {
          this.userName = 'Guest User';
          this.userEmail = 'No Email Provided';
          this.updateUserAvatar(undefined);
        }
        this.cdRef.markForCheck();
      });

    this.userRoleService
      .getUserRole()
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((role: string) => {
        this.userRole = role ?? '';
        this.updateSections();
        this.cdRef.markForCheck();
      });

    this.accountSections = this.navService.getAccountSections() as NavItem[];

    this.checkScreenSize();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        debounceTime(50),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        if (this.currentUrl !== navEnd.urlAfterRedirects) {
          this.currentUrl = navEnd.urlAfterRedirects;
          this.clearActiveLinkCache();
          this.onNavItemClick();
          this.cdRef.markForCheck();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updateUserAvatar(userImage?: string): void {
    this.userAvatarService
      .getFinalAvatarUrl(userImage)
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((avatarUrl: string) => {
        if (avatarUrl && this.userImg !== avatarUrl) {
          this.userImg = avatarUrl;
          this.cdRef.markForCheck();
        }
      });
  }

  private checkScreenSize(): void {
    const width = window?.innerWidth ?? Number.POSITIVE_INFINITY;
    const newCollapsed = width <= this.COLLAPSE_WIDTH;
    if (this.isCollapsed !== newCollapsed) {
      this.isCollapsed = newCollapsed;
      this.isCollapsedEvent.emit(this.isCollapsed);
      this.cdRef.markForCheck();
    }
  }

  toggleSidebar(): void {
    this.isCollapsed = !this.isCollapsed;
    this.isCollapsedEvent.emit(this.isCollapsed);
    this.cdRef.markForCheck();
  }

  isExpanded(title: string): boolean {
    return this.expandedItems.has(title);
  }

  toggleExpand(title: string): void {
    if (this.expandedItems.has(title)) {
      this.expandedItems.delete(title);
    } else {
      this.expandedItems.add(title);
    }
    this.cdRef.markForCheck();
  }

  private clearActiveLinkCache(): void {
    this.activeLinkCache.clear();
  }

  private getCacheKey(item: NavItem): string {
    const params = item.queryParams ? JSON.stringify(item.queryParams) : '';
    return `${item.path || ''}|${params}`;
  }

  isLinkActive(item: NavItem): boolean {
    if (!item.path) return false;

    const cacheKey = this.getCacheKey(item);

    if (this.activeLinkCache.has(cacheKey)) {
      return this.activeLinkCache.get(cacheKey)!;
    }

    const urlParts = this.currentUrl.split('?');
    const currentPath = urlParts[0].replace(/^\//, '');
    const currentParams = new URLSearchParams(urlParts[1] || '');
    const itemPath = item.path.replace(/^\//, '');

    if (currentPath !== itemPath) {
      this.activeLinkCache.set(cacheKey, false);
      return false;
    }

    if (item.queryParams) {
      for (const [key, value] of Object.entries(item.queryParams)) {
        if (currentParams.get(key) !== value) {
          this.activeLinkCache.set(cacheKey, false);
          return false;
        }
      }
      this.activeLinkCache.set(cacheKey, true);
      return true;
    }

    const hasAnyQueryParams = currentParams.toString().length > 0;
    const result = !hasAnyQueryParams;
    this.activeLinkCache.set(cacheKey, result);
    return result;
  }

  isChildActive(item: NavItem): boolean {
    if (!item.children) return false;
    return this.checkChildrenActive(item.children);
  }

  private checkChildrenActive(children: NavItem[]): boolean {
    for (const child of children) {
      if (this.isLinkActive(child)) {
        return true;
      }
      if (child.children && this.checkChildrenActive(child.children)) {
        return true;
      }
    }
    return false;
  }

  private expandCurrentRouteMenu(): void {
    const currentPath = this.currentUrl.split('?')[0].replace(/^\//, '');

    for (const section of this.sections) {
      for (const item of section.children || []) {
        if (this.hasActiveChild(item, currentPath)) {
          this.expandedItems.add(item.title);
          for (const child of item.children || []) {
            if (this.hasActiveChild(child, currentPath)) {
              this.expandedItems.add(child.title);
            }
          }
        }
      }
    }
  }

  private hasActiveChild(item: NavItem, currentPath: string): boolean {
    if (item.path?.replace(/^\//, '') === currentPath) {
      return true;
    }
    for (const child of item.children || []) {
      if (this.hasActiveChild(child, currentPath)) {
        return true;
      }
    }
    return false;
  }

  getNavIcon(item: NavItem): SafeHtml {
    const cacheKey = item.svgIcon || item.icon || 'default';

    if (this.iconCache.has(cacheKey)) {
      return this.iconCache.get(cacheKey)!;
    }

    let result: SafeHtml;
    if (item.svgIcon) {
      result = this.getSafeHtml(item.svgIcon);
    } else {
      result = this.getIconSvg2(item.icon || 'default');
    }

    this.iconCache.set(cacheKey, result);
    return result;
  }

  private getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private getIconSvg2(iconFileName: string): SafeHtml {
    const fileName = iconFileName || 'nav-home.svg';
    const iconName = fileName.replace('.svg', '');

    const imgHtml = `<img src="assets/icons/${fileName}" 
                        class="icon-svg icon-${iconName}" 
                        alt="${iconName}" 
                        style="width: 20px; height: 20px;">`;

    return this.sanitizer.bypassSecurityTrustHtml(imgHtml);
  }

  showNavigation(): void {
    if (this.isClickBlocked || this.suppressHover) return;
    this.isHovered = true;
  }

  hideNavigation(): void {
    this.isHovered = false;
    this.suppressHover = false;
  }

  onNavItemClick(): void {
    this.isHovered = false;
    this.isClickBlocked = true;
    this.suppressHover = true;
    window.setTimeout(() => {
      this.isClickBlocked = false;
    }, 400);
  }

  handleNavClick(event: Event, item: NavItem): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }
    if (item.externalUrl) {
      event.preventDefault();
      window.open(item.externalUrl, '_blank');
      return;
    }
    this.onNavItemClick();
  }

  onParentNavClick(event: Event, item: NavItem): void {
    if (item.disabled) {
      event.preventDefault();
      return;
    }

    if (item.path) {
      this.handleNavClick(event, item);
      return;
    }

    const firstChild = this.findFirstNavigableChild(item);
    if (firstChild) {
      event.preventDefault();
      this.expandedItems.add(item.title);
      this.onNavItemClick();
      this.router.navigate([firstChild.path!], { queryParams: firstChild.queryParams });
      this.cdRef.markForCheck();
      return;
    }

    this.toggleExpand(item.title);
  }

  private findFirstNavigableChild(item: NavItem): NavItem | null {
    const children = item.children ?? [];

    for (const child of children) {
      if (child.path && !child.disabled && !this.hasOpenModalQuery(child.queryParams)) {
        return child;
      }
      if (child.children && child.children.length > 0) {
        const nested = this.findFirstNavigableChild(child);
        if (nested) return nested;
      }
    }

    for (const child of children) {
      if (child.path && !child.disabled) {
        return child;
      }
      if (child.children && child.children.length > 0) {
        const nested = this.findFirstNavigableChild(child);
        if (nested) return nested;
      }
    }

    return null;
  }

  private hasOpenModalQuery(queryParams?: { [key: string]: string }): boolean {
    return queryParams?.['openModal'] === 'true';
  }

  resetHoverState(): void {
    this.isClickBlocked = true;
    window.setTimeout(() => {
      this.isClickBlocked = false;
    }, 300);
  }


  checkSamePath(subSectionPath?: string): boolean {
    if (!subSectionPath) {
      return false;
    }
    const url = this.currentUrl.split('?')[0].split('#')[0];
    const segments = url.split('/').filter(Boolean);
    const lastSegment = segments.length ? segments[segments.length - 1] : '';
    return lastSegment === subSectionPath;
  }

  clearSummaryData(): void {
    try {
      localStorage.removeItem(this.SUMMARY_KEY);
      localStorage.removeItem(this.RETURN_TRANSFER_KEY);
    } catch (e) {
      console.warn('clearSummaryData: localStorage not available', e);
    }
  }

  updateSections(): void {
    const allSections = (this.navService.getSections() ?? []) as NavItem[];
    const normalizedRole = (this.userRole ?? '').toLowerCase();

    this.sections = allSections
      .map((section: NavItem) => {
        const cloned: NavItem = { ...section };

        if (Array.isArray(section.children) && section.children.length > 0) {
          cloned.children = this.filterNavItemsByRole(section.children, normalizedRole);
        }

        return cloned;
      })
      .filter((s) => !s.children || s.children.length > 0);
  }

  private filterNavItemsByRole(items: NavItem[], normalizedRole: string): NavItem[] {
    return items
      .filter((item) => {
        if (!item.allowedRoles || item.allowedRoles.length === 0) {
          return true;
        }
        return item.allowedRoles.some(
          (r) => (r ?? '').toLowerCase() === normalizedRole
        );
      })
      .map((item) => {
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: this.filterNavItemsByRole(item.children, normalizedRole)
          };
        }
        return item;
      });
  }

  trackBySection(index: number, section: NavItem): string {
    return section.title || `section-${index}`;
  }

  trackByNavItem(index: number, item: NavItem): string {
    return `${item.title}-${item.path || index}`;
  }

  trackBySubmenuItem(index: number, item: NavItem): string {
    return `${item.title}-${item.path || index}`;
  }
}
